/**
 * /erp/dispatches/{dispatchId}/deliveries — full-screen deliveries editor
 * (Manager+ for PR-F).
 *
 * Only valid while the dispatch is still ``draft``. Once confirmed, the
 * server rejects the PUT replace with ``dispatch_not_editable`` — we
 * also redirect back to the detail page so the user doesn't waste
 * effort. The form re-uses the shared ``DispatchDeliveriesEditor`` so
 * the create flow and the edit flow behave identically.
 *
 * The save uses a full replace pattern: the client builds the desired
 * final list of deliveries (and their lines) and PUTs it. The server
 * deletes any old children that weren't kept and inserts new ones in
 * a single transaction.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, Loader2, Save, Truck, X } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import {
  DispatchDeliveriesEditor,
  type DraftDelivery,
  makeEmptyLine,
} from "../components/DispatchDeliveriesEditor";
import {
  ApiError,
  type ErpDispatchReplaceDeliveries,
  accountsApi,
  contactsApi,
  erpApi,
  isErpDispatchConflict,
} from "../lib/api";
import { useDispatch, useOrders, useProducts, useRawMaterials, useWarehouses } from "../lib/erp";
import { toast } from "../lib/toast";
import { cleanQtyInput } from "../lib/units";

export const Route = createFileRoute("/_app/erp/dispatches/$dispatchId/deliveries")({
  component: EditDeliveriesPage,
});

function EditDeliveriesPage() {
  const { dispatchId } = useParams({ from: "/_app/erp/dispatches/$dispatchId/deliveries" });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatchQ = useDispatch(dispatchId);

  const warehousesQ = useWarehouses();
  const productsQ = useProducts();
  const rawsQ = useRawMaterials();
  const ordersQ = useOrders({ limit: 200 });
  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200 }],
    queryFn: () => accountsApi.list({ limit: 200 }),
  });
  const contactsQ = useQuery({
    queryKey: ["contacts", { limit: 200, offset: 0 }],
    queryFn: () => contactsApi.list({ limit: 200, offset: 0 }),
  });

  const [draft, setDraft] = useState<DraftDelivery[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  const dispatchableOrders = (ordersQ.data?.items ?? []).filter(
    (o) => o.status === "ready_for_dispatch" || o.status === "partially_fulfilled",
  );

  // Hydrate the draft from the dispatch on first successful load.
  useEffect(() => {
    if (!dispatchQ.data) return;
    if (hydrated) return;
    setDraft(
      dispatchQ.data.deliveries.map(
        (d): DraftDelivery => ({
          uiId: `srv-${d.id}`,
          kind: d.kind,
          source_warehouse_id: d.source_warehouse_id,
          dest_warehouse_id: d.dest_warehouse_id ?? "",
          account_id: d.account_id ?? "",
          contact_id: d.contact_id ?? "",
          order_id: d.order_id ?? "",
          delivery_address: d.delivery_address ?? "",
          notes: d.notes ?? "",
          lines: d.lines.length
            ? d.lines.map((l) => ({
                uiId: `srv-${l.id}`,
                item_kind: l.item_kind,
                item_id: l.product_id ?? l.raw_material_id ?? "",
                custom_name: l.custom_name ?? "",
                custom_uom: l.item_uom ?? "",
                qty: cleanQtyInput(l.qty, l.item_uom),
                unit_price: l.unit_price ?? "",
              }))
            : [makeEmptyLine()],
        }),
      ),
    );
    setHydrated(true);
  }, [dispatchQ.data, hydrated]);

  const mutation = useMutation({
    mutationFn: (body: ErpDispatchReplaceDeliveries) =>
      erpApi.dispatches.replaceDeliveries(dispatchId, body),
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: ["erp", "dispatches"] });
      toast.success(`${d.code}: deliveries saved.`);
      navigate({ to: "/erp/dispatches/$dispatchId", params: { dispatchId } });
    },
    onError: (err) => {
      if (err instanceof ApiError && isErpDispatchConflict(err.detail)) {
        setError(err.detail.message);
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not save deliveries.";
      setError(msg);
      toast.error(msg);
    },
  });

  if (dispatchQ.isLoading) {
    return (
      <div className="page-shell p-4 sm:p-6">
        <p className="flex items-center gap-2 text-sm opacity-60">
          <Loader2 className="size-4 animate-spin" aria-hidden /> Loading dispatch…
        </p>
      </div>
    );
  }
  if (dispatchQ.error || !dispatchQ.data) {
    return (
      <div className="page-shell p-4 sm:p-6">
        <Link
          to="/erp/dispatches"
          className="inline-flex items-center gap-1 text-xs opacity-70 hover:underline"
        >
          <ArrowLeft className="size-3" aria-hidden /> Dispatches
        </Link>
        <p className="mt-3 text-sm text-red-700 dark:text-red-300">Could not load dispatch.</p>
      </div>
    );
  }
  const d = dispatchQ.data;

  // Edits only make sense on drafts; if the dispatch has progressed,
  // bounce back so the user doesn't make changes that'd 409.
  if (d.status !== "draft") {
    return (
      <div className="page-shell space-y-3 p-4 sm:p-6">
        <Link
          to="/erp/dispatches/$dispatchId"
          params={{ dispatchId }}
          className="inline-flex items-center gap-1 text-xs opacity-70 hover:underline"
        >
          <ArrowLeft className="size-3" aria-hidden /> {d.code}
        </Link>
        <div className="rounded-xl border border-[var(--border)] p-4 text-sm">
          <p className="font-medium">Deliveries are frozen.</p>
          <p className="mt-1 opacity-70">
            Once a dispatch is confirmed, lines and recipients freeze for audit-trail integrity. To
            change them, cancel this dispatch and create a new one.
          </p>
        </div>
      </div>
    );
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const issue = validateDraftDeliveries(draft);
    if (issue) {
      setError(issue);
      return;
    }

    const body: ErpDispatchReplaceDeliveries = {
      deliveries: draft.map((dlv, idx) => ({
        kind: dlv.kind,
        source_warehouse_id: dlv.source_warehouse_id,
        ...(dlv.kind === "internal"
          ? { dest_warehouse_id: dlv.dest_warehouse_id || null }
          : {
              account_id: dlv.account_id || null,
              contact_id: dlv.contact_id || null,
              order_id: dlv.order_id || null,
              delivery_address: dlv.delivery_address.trim() ? dlv.delivery_address.trim() : null,
            }),
        notes: dlv.notes.trim() ? dlv.notes.trim() : null,
        position: idx,
        lines: dlv.lines.map((ln, li) => ({
          item_kind: ln.item_kind,
          ...(ln.item_kind === "product"
            ? { product_id: ln.item_id || null, raw_material_id: null }
            : ln.item_kind === "custom"
              ? {
                  product_id: null,
                  raw_material_id: null,
                  custom_name: ln.custom_name?.trim() || null,
                  custom_uom: ln.custom_uom?.trim() || null,
                }
              : { product_id: null, raw_material_id: ln.item_id || null }),
          qty: ln.qty.trim(),
          ...(dlv.kind === "external"
            ? { unit_price: ln.unit_price.trim() ? ln.unit_price.trim() : null }
            : { unit_price: null }),
          position: li,
        })),
      })),
    };

    mutation.mutate(body);
  };

  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <Link
        to="/erp/dispatches/$dispatchId"
        params={{ dispatchId }}
        className="inline-flex items-center gap-1 text-xs opacity-70 hover:underline"
      >
        <ArrowLeft className="size-3" aria-hidden /> {d.code}
      </Link>

      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Truck className="size-5 opacity-70" aria-hidden />
          Edit deliveries
        </h1>
        <p className="text-sm opacity-60">
          Edits land in one transaction. Saving replaces the entire delivery + line set on{" "}
          <span className="rounded-md bg-black/5 px-1 py-0.5 font-mono text-[11px] dark:bg-white/10">
            {d.code}
          </span>
          .
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-4">
        <DispatchDeliveriesEditor
          deliveries={draft}
          onChange={setDraft}
          warehouses={warehousesQ.data?.items ?? []}
          products={productsQ.data?.items ?? []}
          rawMaterials={rawsQ.data?.items ?? []}
          accounts={accountsQ.data?.items ?? []}
          contacts={contactsQ.data?.items ?? []}
          orders={dispatchableOrders}
        />

        {error && (
          <p className="flex items-center gap-1 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-200">
            <AlertCircle className="size-3" aria-hidden />
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Link
            to="/erp/dispatches/$dispatchId"
            params={{ dispatchId }}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden /> Cancel
          </Link>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Save className="size-4" aria-hidden />
            )}
            Save deliveries
          </button>
        </div>
      </form>
    </div>
  );
}

function validateDraftDeliveries(deliveries: DraftDelivery[]): string | null {
  for (const [i, d] of deliveries.entries()) {
    const label = `Delivery #${i + 1}`;
    if (!d.source_warehouse_id) return `${label}: pick a source warehouse.`;
    if (d.kind === "internal") {
      if (!d.dest_warehouse_id) {
        return `${label}: internal deliveries need a destination warehouse.`;
      }
      if (d.dest_warehouse_id === d.source_warehouse_id) {
        return `${label}: source and destination warehouses must differ.`;
      }
    }
    if (d.kind === "external" && !d.account_id && !d.contact_id) {
      return `${label}: external deliveries need at least one of account / contact.`;
    }
    if (d.lines.length === 0) {
      return `${label}: add at least one line, or remove the delivery.`;
    }
    for (const [j, ln] of d.lines.entries()) {
      const lLabel = `${label}, line #${j + 1}`;
      if (!ln.item_id) return `${lLabel}: pick a product or raw material.`;
      if (!ln.qty.trim() || Number(ln.qty) <= 0) {
        return `${lLabel}: qty must be positive.`;
      }
      if (d.kind === "external") {
        if (!ln.unit_price.trim()) {
          return `${lLabel}: external deliveries require a unit price.`;
        }
        if (Number(ln.unit_price) < 0) {
          return `${lLabel}: unit price must be ≥ 0.`;
        }
      }
    }
  }
  return null;
}
